import { gsap } from 'gsap';

// One pixel below $breakpoint-header-nav (scss/abstracts/_variables.scss) so JS matches CSS min-width breakpoint.
const MOBILE_MQ = window.matchMedia( '(max-width: 1359px)' );

function lockScroll()   { document.documentElement.style.overflowY = 'hidden'; }
function unlockScroll() { document.documentElement.style.overflowY = ''; }

// Selector for tabbable elements inside the nav panel. Used for focus trap and
// initial focus placement.
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function initMobileNav() {
  const toggle = document.querySelector( '.ul-nav-toggle' );
  const nav    = document.querySelector( '.ul-site-nav' );

  if ( ! toggle || ! nav ) return;

  const items = nav.querySelectorAll( '.ul-site-nav__list > li' );
  let isOpen  = false;

  // Let GSAP own the transform so it can animate from a known state.
  // Only set on mobile — on desktop the nav is a normal flex child.
  if ( MOBILE_MQ.matches ) {
    gsap.set( nav, { yPercent: -100 } );
  }

  // Reset when crossing the breakpoint
  MOBILE_MQ.addEventListener( 'change', e => {
    if ( e.matches ) {
      gsap.set( nav, { yPercent: -100, pointerEvents: 'none' } );
    } else {
      gsap.set( nav, { clearProps: 'all' } );
    }

    if ( isOpen ) unlockScroll();
    isOpen = false;
    document.body.classList.remove( 'nav-is-open' );
    toggle.setAttribute( 'aria-expanded', 'false' );
  } );

  toggle.addEventListener( 'click', () => isOpen ? close() : open() );

  // Escape closes the panel; Tab/Shift+Tab cycle focus within it.
  document.addEventListener( 'keydown', e => {
    if ( ! isOpen ) return;

    if ( e.key === 'Escape' ) {
      e.preventDefault();
      close();
      return;
    }

    if ( e.key === 'Tab' ) {
      const focusable = nav.querySelectorAll( FOCUSABLE );
      if ( ! focusable.length ) return;
      const first = focusable[ 0 ];
      const last  = focusable[ focusable.length - 1 ];

      if ( e.shiftKey && document.activeElement === first ) {
        e.preventDefault();
        last.focus();
      } else if ( ! e.shiftKey && document.activeElement === last ) {
        e.preventDefault();
        first.focus();
      }
    }
  } );

  function open() {
    isOpen = true;
    document.body.classList.add( 'nav-is-open' );
    toggle.setAttribute( 'aria-expanded', 'true' );
    lockScroll();

    gsap.to( nav, {
      yPercent: 0,
      duration: 0.4,
      ease: 'power2.out',
      pointerEvents: 'auto',
    } );

    gsap.from( items, {
      opacity: 0,
      y: 10,
      duration: 0.3,
      stagger: 0.08,
      delay: 0.45,
      ease: 'power2.out',
      clearProps: 'opacity,transform',
      onComplete() {
        const firstLink = nav.querySelector( FOCUSABLE );
        if ( firstLink ) firstLink.focus();
      },
    } );
  }

  function close() {
    isOpen = false;
    document.body.classList.remove( 'nav-is-open' );
    toggle.setAttribute( 'aria-expanded', 'false' );

    // Return focus to the toggle so keyboard users land where they started.
    toggle.focus();

    // Fade items out first, then slide panel away
    gsap.to( items, {
      opacity: 0,
      duration: 0.15,
      ease: 'power2.in',
      onComplete() {
        gsap.to( nav, {
          yPercent: -100,
          duration: 0.3,
          ease: 'power2.in',
          pointerEvents: 'none',
          onComplete() {
            gsap.set( items, { opacity: 1 } );
            unlockScroll();
          },
        } );
      },
    } );
  }
}
